import clsx from 'clsx';
import { Children, type HTMLAttributes, type ReactNode } from 'react';
import { z } from 'zod';
import type { GridGap, GridRowAlign } from './Grid';
import {
  gridGapSchema,
  gridRowAlignSchema,
  MAX_GRID_ROW_COLUMNS,
} from './Grid';

import styles from './GridRow.module.scss';

export const gridRowPropsSchema = z
  .object({
    gap: gridGapSchema.optional(),
    align: gridRowAlignSchema.optional(),
  })
  .strict();

export type GridRowDataProps = z.infer<typeof gridRowPropsSchema>;

export interface GridRowProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    GridRowDataProps {
  children?: ReactNode;
}

const gapClassMap: Record<GridGap, string> = {
  sm: styles.gapSm,
  md: styles.gapMd,
  lg: styles.gapLg,
};

const alignClassMap: Record<GridRowAlign, string> = {
  start: styles.alignStart,
  center: styles.alignCenter,
  stretch: styles.alignStretch,
};

const getColumnClass = (columnCount: number): string =>
  styles[`cols${columnCount}` as keyof typeof styles] ?? styles.cols1;

export const GridRow = ({
  gap,
  align = 'stretch',
  className,
  children,
  ...rest
}: GridRowProps) => {
  const childArray = Children.toArray(children).filter(Boolean);
  const columnCount = Math.min(
    Math.max(childArray.length, 1),
    MAX_GRID_ROW_COLUMNS
  );

  return (
    <div
      data-testid="grid-row"
      className={clsx(
        styles.root,
        getColumnClass(columnCount),
        gap != null && gapClassMap[gap],
        alignClassMap[align],
        className
      )}
      {...rest}
    >
      {childArray.map((child, index) => (
        <div key={index} className={styles.cell}>
          {child}
        </div>
      ))}
    </div>
  );
};
